import { Alert, Body, BodySm, Button, Caption, Label, Progress, Tooltip } from "@pej/kore/components";
import { FolderOpen, RotateCcw, TriangleAlert, Upload, X } from "lucide-react";
import { type ChangeEvent, type DragEvent, useRef, useState } from "react";
import { ASSET_KIND, type AssetKindCopy } from "./asset-kind";
import type { AssetRejectionReason, AssetSummary, AssetType, UploadPhase } from "./asset-types";
import { FontSpecimen } from "./font-specimen";

/**
 * The form control that holds at most one asset (spec §3.1). It is also the
 * drop target: dropping a file hands it straight to `onFiles`, so the common
 * case — "I have a file, put it on this template" — never opens the picker.
 *
 * Renders one of five states, driven by `phase` first and `value` second:
 * empty · ready (a value, phase idle) · transferring · checking · rejected.
 * `phase` takes priority over `value` because an in-flight or failed upload
 * is what the operator needs to see even when a prior asset is still set.
 */
export interface AssetFieldProps {
    /** Optional: omitted when the card already titles the field (the Logo card is
     *  a single-field card, so its own title/description are the field's heading —
     *  a field-level label there would just repeat them). */
    label?: string;
    hint?: string;
    /** Which kind of file this field holds: decides what it accepts and says. */
    assetType: AssetType;
    value: AssetSummary | null;
    phase: UploadPhase;
    canManage: boolean;
    onFiles: (files: FileList) => void;
    onOpenPicker: () => void;
    onClear: () => void;
    onCancelUpload: () => void;
    folderNameFor: (folderId: string | null) => string | null;
    /** The committed asset prints too small to be sharp — surfaces a warning
     *  icon beside the size line (with the detail on hover). */
    lowRes?: boolean;
}

// Copy table is verbatim from the spec. Headline is a function rather than a
// plain string only for IMAGE_DIMENSIONS_EXCEEDED, whose sentence carries the
// service-supplied `detail`; the others ignore the argument.
function rejectionCopy(
    kind: AssetKindCopy,
): Record<AssetRejectionReason, { headline: (detail: string | undefined) => string; action: string }> {
    return {
        CONTENT_TYPE_MISMATCH: {
            headline: () => kind.notWhatItSays,
            action: "Choose a different file",
        },
        INVALID_CONTENT: {
            headline: () => kind.notValid,
            action: "Choose a different file",
        },
        IMAGE_DIMENSIONS_EXCEEDED: {
            headline: (detail) => `That image is too large — ${detail ?? "check the size requirements"}.`,
            action: "Choose a different file",
        },
        CONTENT_MISSING: {
            headline: () => "The upload didn't arrive. This one is worth retrying.",
            action: "Retry",
        },
    };
}

// A local join, not a re-implementation of kore's `cn()`: this directory
// can't import tailwind-merge (import-boundary guard), and wrapping a kore
// class constant in twMerge is a known way to strip its `text-kore-*` token
// anyway, so a plain join is the safer default even where merging isn't at
// stake.
function cx(...parts: Array<string | false | null | undefined>): string {
    return parts.filter(Boolean).join(" ");
}

export function AssetField({
    label,
    hint,
    assetType,
    value,
    phase,
    canManage,
    onFiles,
    onOpenPicker,
    onClear,
    onCancelUpload,
    folderNameFor,
    lowRes,
}: AssetFieldProps) {
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const openFileDialog = () => inputRef.current?.click();

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) onFiles(event.target.files);
        // Reset so re-selecting the same file still fires a change event.
        event.target.value = "";
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        // Both dragover and drop need preventDefault, or the browser
        // navigates to the dropped file instead of handing it to us.
        event.preventDefault();
        if (!canManage) return;
        setDragActive(true);
    };

    const handleDragLeave = () => setDragActive(false);

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragActive(false);
        if (!canManage) return;
        if (event.dataTransfer.files.length > 0) onFiles(event.dataTransfer.files);
    };

    const idle = phase.phase === "idle";
    const kind = ASSET_KIND[assetType];

    return (
        <div className="space-y-2">
            {(label || hint) && (
                <div>
                    {label && <Label>{label}</Label>}
                    {hint && (
                        <Caption as="p" className="mt-0.5">
                            {hint}
                        </Caption>
                    )}
                </div>
            )}

            {/* Hidden input backs both "Upload file" and the rejected-state
                retry/choose-different actions — one dialog, several triggers. */}
            <input
                ref={inputRef}
                type="file"
                accept={kind.accept}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={handleChange}
            />

            {idle && (
                // biome-ignore lint/a11y/noStaticElementInteractions: pointer drop target only; both buttons inside are the keyboard-accessible way to set a file, so this div needs no interactive role of its own
                <div
                    data-testid="asset-dropzone"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={cx(
                        "flex min-h-24 items-center rounded-kore-control border border-dashed p-4 transition-colors",
                        dragActive ? "border-kore-accent bg-kore-accent/5" : "border-kore-border",
                        // Give the *empty* drop target a faint recessed fill so it reads as a "well"
                        // to drop into rather than a hairline outline lost on the card. Once a file is
                        // chosen the container is just a frame around it — a fill there would look
                        // unfinished — and on drag-over the accent wash above takes over.
                        // Per-theme: surface-soft sits right on the dark card but reads heavy on white,
                        // so light mixes it 60% toward the card while dark keeps the full token.
                        !dragActive && !value && "bg-kore-surface-soft/60 dark:bg-kore-surface-soft",
                    )}
                >
                    {value ? (
                        <ReadyContent
                            value={value}
                            folderNameFor={folderNameFor}
                            canManage={canManage}
                            onBrowse={onOpenPicker}
                            onUpload={openFileDialog}
                            onClear={onClear}
                            lowRes={lowRes}
                        />
                    ) : (
                        <EmptyContent
                            prompt={kind.dropPrompt}
                            canManage={canManage}
                            onBrowse={onOpenPicker}
                            onUpload={openFileDialog}
                        />
                    )}
                </div>
            )}

            {phase.phase === "transferring" && (
                <TransferringContent phase={phase} canManage={canManage} onCancel={onCancelUpload} />
            )}
            {phase.phase === "checking" && (
                <CheckingContent phase={phase} canManage={canManage} onCancel={onCancelUpload} />
            )}
            {phase.phase === "rejected" && (
                <RejectedContent phase={phase} kind={kind} canManage={canManage} onAction={openFileDialog} />
            )}
        </div>
    );
}

function EmptyContent({
    prompt,
    canManage,
    onBrowse,
    onUpload,
}: {
    prompt: string;
    canManage: boolean;
    onBrowse: () => void;
    onUpload: () => void;
}) {
    return (
        <div className="flex w-full flex-col items-center gap-3 text-center">
            <BodySm className="text-kore-text-muted">{prompt}</BodySm>
            <div className="flex gap-2">
                {/* Upload is a manage-only affordance — absent, not disabled,
                    so a read-only operator never sees a control they can't use. */}
                {canManage && (
                    <Button type="button" variant="outline" size="sm" onClick={onUpload}>
                        <Upload aria-hidden="true" />
                        Upload file
                    </Button>
                )}
                <Button type="button" variant={canManage ? "ghost" : "outline"} size="sm" onClick={onBrowse}>
                    <FolderOpen aria-hidden="true" />
                    Choose existing
                </Button>
            </div>
        </div>
    );
}

function ReadyContent({
    value,
    folderNameFor,
    canManage,
    onBrowse,
    onUpload,
    onClear,
    lowRes,
}: {
    value: AssetSummary;
    folderNameFor: (folderId: string | null) => string | null;
    canManage: boolean;
    onBrowse: () => void;
    onUpload: () => void;
    onClear: () => void;
    lowRes?: boolean;
}) {
    const folderName = folderNameFor(value.folderId);
    const hasDimensions = value.width != null && value.height != null;

    return (
        <div className="flex w-full items-center gap-3">
            <div className="size-14 shrink-0 overflow-hidden rounded-kore-control bg-kore-surface-tint">
                {value.type === "FONT" ? (
                    <FontSpecimen
                        url={value.thumbnailUrl}
                        label={value.displayName}
                        className="flex size-full items-center justify-center text-xl text-kore-text"
                    />
                ) : (
                    value.thumbnailUrl && (
                        <img src={value.thumbnailUrl} alt={value.displayName} className="size-full object-cover" />
                    )
                )}
            </div>
            <div className="min-w-0 flex-1">
                <Body as="p" className="truncate font-medium">
                    {value.displayName}
                </Body>
                <div className="flex min-w-0 items-center gap-1">
                    <Caption as="p" className="min-w-0 truncate">
                        {hasDimensions ? `${value.width} × ${value.height}` : null}
                        {folderName ? ` · ${folderName}` : null}
                    </Caption>
                    {/* Low-res rides an icon by the size line, not a text row below
                        the field — that row shifted the whole field on every render.
                        The detail is on hover. */}
                    {lowRes && hasDimensions && (
                        <Tooltip>
                            <Tooltip.Trigger
                                render={
                                    <span
                                        role="img"
                                        aria-label="Smaller than the print size"
                                        className="inline-flex shrink-0 cursor-help text-status-warn-dot"
                                    >
                                        <TriangleAlert className="size-3.5" aria-hidden="true" />
                                    </span>
                                }
                            />
                            <Tooltip.Content variant="inverse">
                                This image is smaller than the voucher prints at — it will look soft on paper, and the
                                preview cannot show that.
                            </Tooltip.Content>
                        </Tooltip>
                    )}
                </div>
            </div>
            <div className="flex shrink-0 gap-0.5">
                {canManage && (
                    <Button type="button" variant="ghost" size="icon" onClick={onUpload} aria-label="Upload file">
                        <Upload aria-hidden="true" />
                    </Button>
                )}
                <Button type="button" variant="ghost" size="icon" onClick={onBrowse} aria-label="Choose existing">
                    <FolderOpen aria-hidden="true" />
                </Button>
                {/* "Clear" only unsets this field's reference to the asset — it
                    never deletes the asset itself, so it must never read "delete". */}
                <Button type="button" variant="ghost" size="icon" onClick={onClear} aria-label="Clear">
                    <X aria-hidden="true" />
                </Button>
            </div>
        </div>
    );
}

function TransferringContent({
    phase,
    canManage,
    onCancel,
}: {
    phase: Extract<UploadPhase, { phase: "transferring" }>;
    canManage: boolean;
    onCancel: () => void;
}) {
    // Byte transfer is measurable, so it gets a real percentage — computed here
    // rather than handed raw bytes to Progress, which would otherwise report
    // aria-valuenow as a byte count instead of a percentage.
    const percent = phase.totalBytes > 0 ? Math.round((phase.sentBytes / phase.totalBytes) * 100) : 0;

    return (
        <div className="space-y-2 rounded-kore-control border border-kore-border p-4">
            <div className="flex items-center justify-between gap-2">
                <BodySm className="truncate">{phase.fileName}</BodySm>
                {canManage && (
                    <Button type="button" variant="ghost" size="icon-sm" onClick={onCancel} aria-label="Cancel upload">
                        <X aria-hidden="true" />
                    </Button>
                )}
            </div>
            <Progress value={percent} label={`Uploading — ${percent}%`} />
        </div>
    );
}

function CheckingContent({
    phase,
    canManage,
    onCancel,
}: {
    phase: Extract<UploadPhase, { phase: "checking" }>;
    canManage: boolean;
    onCancel: () => void;
}) {
    return (
        <div className="space-y-2 rounded-kore-control border border-kore-border p-4">
            <div className="flex items-center justify-between gap-2">
                <BodySm className="truncate">{phase.fileName}</BodySm>
                {canManage && (
                    <Button type="button" variant="ghost" size="icon-sm" onClick={onCancel} aria-label="Cancel upload">
                        <X aria-hidden="true" />
                    </Button>
                )}
            </div>
            {/* No value: the asset service exposes no progress signal for
                verification, so an indeterminate bar is honest and a fake
                percentage would not be. */}
            <Progress label="Checking the file…" />
        </div>
    );
}

function RejectedContent({
    phase,
    kind,
    canManage,
    onAction,
}: {
    phase: Extract<UploadPhase, { phase: "rejected" }>;
    kind: AssetKindCopy;
    canManage: boolean;
    onAction: () => void;
}) {
    const copy = rejectionCopy(kind)[phase.reason];

    return (
        <Alert variant="destructive">
            <Alert.Icon>
                <TriangleAlert />
            </Alert.Icon>
            <Alert.Description>
                <p>{copy.headline(phase.detail)}</p>
                {canManage && (
                    <Button type="button" variant="outline" size="sm" className="mt-2" onClick={onAction}>
                        {phase.reason === "CONTENT_MISSING" && <RotateCcw aria-hidden="true" />}
                        {copy.action}
                    </Button>
                )}
            </Alert.Description>
        </Alert>
    );
}
